import React, { useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  MenuButton,
  ToggleButton,
  SpinButton,
  TabList,
  Tab,
  AvatarGroup,
  AvatarGroupItem,
} from '@fluentui/react-components';
import { colorTokens, shadowTokens, spacingTokens, sizeTokens, typographyTokens, breakpointTokens } from '../../../tokens';
import { useWordSlateEditor } from './WordSlateEditorContext';

const useStyles = makeStyles({
  container: {
    backgroundColor: colorTokens.neutralBackground3, // Neutral/Background/3/Rest
    padding: `0 ${spacingTokens.spacingHorizontalS}`,
    margin: 0, // Explicitly remove any default margins
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1000, // Higher than CopilotPane (999) so ribbon shadow appears over pane
    // Removed minHeight to eliminate unused space
    // Set Word brand colors as CSS custom properties for Fluent UI components
    '--colorBrandBackground': colorTokens.wordBrand,
    '--colorBrandBackgroundHover': colorTokens.wordBrandAlt,
    '--colorBrandBackgroundPressed': colorTokens.wordBrand,
    '--colorBrandForeground1': colorTokens.wordBrand,
    '--colorBrandForeground2': colorTokens.wordBrandAlt,
    // Word brand colors for tab selection indicator
    '--colorCompoundBrandForeground1': colorTokens.wordBrand,
    '--colorCompoundBrandBackground': colorTokens.wordBrand,
    '--colorNeutralForeground2BrandSelected': colorTokens.wordBrand,
    // Global MenuButton width override
    '& .fui-MenuButton': {
      minWidth: '48px',
      width: 'fit-content',
      maxWidth: 'none',
    },
    '& .fui-MenuButton__root': {
      minWidth: '48px',
      width: 'fit-content',
      maxWidth: 'none',
      padding: '4px 2px',
    },
    '& .fui-MenuButton__menuButton': {
      minWidth: '48px',
      width: 'fit-content',
      maxWidth: 'none',
      padding: '4px 2px',
    },
  },
  
  // Tab Bar Section
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: spacingTokens.spacingVerticalXS,
    minWidth: 0, // Allow flex shrinking
    overflow: 'hidden',
    backgroundColor: colorTokens.neutralBackground3,
    height: '40px', // Fixed height to prevent vertical jumping
    flexShrink: 0, // Prevent the tab bar from shrinking
  },
  
  tabList: {
    display: 'flex',
    alignItems: 'center',
    gap: spacingTokens.spacingHorizontalXS, // Remove gap to allow tabs to be closer
    paddingLeft: spacingTokens.spacingHorizontalXS,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    borderBottom: 'none', // Let individual tabs handle their own borders
    backgroundColor: colorTokens.neutralBackground3,
  },
  
  tab: {
    // Word brand styling for individual tabs - indicator only
    width: 'fit-content',
    minWidth: 'auto',
    paddingBottom: spacingTokens.spacingVerticalXS,
    // Keep hover effects for non-active tabs
    '&:hover:not([aria-selected="true"])': {
      color: `${colorTokens.wordBrandAlt} !important`,
    },
    // Override any Fluent UI tab indicator styling
    '& .fui-Tab__content::after': {
      backgroundColor: `${colorTokens.wordBrand} !important`,
    },
  },

  // Word-specific primary button styling for Share button
  wordPrimaryButton: {
    backgroundColor: colorTokens.wordBrand,
    color: colorTokens.neutralForegroundOnBrand,
    '&:hover': {
      backgroundColor: colorTokens.wordBrandAlt,
    },
    '&:active': {
      backgroundColor: colorTokens.wordBrand,
    },
  },
  
  // Collaboration Corner
  collabCorner: {
    display: 'flex',
    alignItems: 'center',
    gap: spacingTokens.spacingHorizontalS,
    // Remove background to let Fluent UI button styles show properly
  },

  // Avatar Group Styles - responsive behavior only, AvatarGroup handles layout
  avatarGroup: {
    [`@media (max-width: ${breakpointTokens.tablet})`]: {
      display: 'none',
    },
  },
  
  collabButton: {
    width: 'fit-content',
    minWidth: sizeTokens.buttonMinWidth, // 32px minimum width for consistency
    fontSize: typographyTokens.fontSizeBase200,
    // Let Fluent UI size="small" handle the 24px height naturally
    // When labels are hidden on smaller screens, force main button to 32px
    [`@media (max-width: ${breakpointTokens.desktopXL})`]: {
      '& > button:first-child': {
        width: sizeTokens.buttonMinWidth,
        minWidth: sizeTokens.buttonMinWidth,
        padding: spacingTokens.spacingVerticalNone,
      },
    },
  },

  // Icon-only collaboration buttons should have fixed 32px width for the main button
  collabButtonIcon: {
    width: 'fit-content',
    minWidth: 'auto',
    fontSize: typographyTokens.fontSizeBase200,
    // Let Fluent UI size="small" handle the 24px height naturally
    // Target the main button part of the MenuButton
    '& > button:first-child': {
      width: sizeTokens.buttonMinWidth, // Force 32px width for icon-only buttons
      minWidth: sizeTokens.buttonMinWidth,
      padding: spacingTokens.spacingVerticalNone,
    },
  },

  // Responsive button labels - hide text on smaller viewports
  buttonLabel: {
    fontSize: typographyTokens.fontSizeBase300,
    fontFamily: typographyTokens.fontFamilyBase,
    fontWeight: typographyTokens.fontWeightRegular,
    color: colorTokens.neutralForeground1,
    lineHeight: typographyTokens.lineHeightBase300,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    [`@media (max-width: ${breakpointTokens.desktop})`]: {
      display: 'none',
    },
  },

  // Collaboration corner button labels - matches Share button styling
  collabButtonLabel: {
    fontSize: typographyTokens.fontSizeBase200,
    fontFamily: typographyTokens.fontFamilyBase,
    fontWeight: typographyTokens.fontWeightRegular,
    color: 'inherit',
    lineHeight: typographyTokens.lineHeightBase200,
    whiteSpace: 'nowrap',
    marginLeft: spacingTokens.spacingHorizontalXXS, // Space between icon and label
    [`@media (max-width: ${breakpointTokens.desktopXL})`]: {
      display: 'none',
    },
    [`@media (max-width: ${breakpointTokens.desktop})`]: {
      display: 'none',
    },
  },

  // Share button label - always visible regardless of screen size
  shareButtonLabel: {
    fontSize: typographyTokens.fontSizeBase200,
    fontFamily: typographyTokens.fontFamilyBase,
    fontWeight: typographyTokens.fontWeightRegular,
    color: 'inherit',
    lineHeight: typographyTokens.lineHeightBase200,
    whiteSpace: 'nowrap',
    marginLeft: spacingTokens.spacingHorizontalXXS, // Space between icon and label
    // No responsive breakpoints - always visible
  },


  // Ribbon Content
  ribbonContent: {
    backgroundColor: colorTokens.neutralBackground1,
    borderRadius: '12px',
    padding: `${spacingTokens.spacingVerticalNone} ${spacingTokens.spacingHorizontalS}`, // Remove vertical padding to be flush
    display: 'flex',
    alignItems: 'center',
    gap: spacingTokens.spacingHorizontalNone,
    overflow: 'hidden',
    height: '44px', // Fixed height instead of minHeight
    boxShadow: shadowTokens.shadow4, // Subtle shadow for depth
  },
  
  ribbonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: spacingTokens.spacingHorizontalNone,
    // Ensure child elements can receive pointer events
    pointerEvents: 'auto',
  },
  
  ribbonMenuButton: {
    // Force exact 48px minimum width for MenuButtons
    minWidth: '48px',
    width: 'fit-content',
    maxWidth: 'none',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacingTokens.spacingHorizontalXXS,
    padding: '4px',
  },

  ribbonButton: {
  // Force exact 32px minimum width for ButtonIcons
    minWidth: '32px',
    width: 'fit-content',
    maxWidth: 'none',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacingTokens.spacingHorizontalXXS,
    padding: '4px',
    
    // Checked state styling with consistent background and stroke
    '&[aria-pressed="true"]': {
      backgroundColor: colorTokens.subtleBackgroundSelected,
      border: `1px solid ${colorTokens.strokeAccessible}`,
      boxSizing: 'border-box',
    },
  },

  icon: {
    width: sizeTokens.iconMedium,
    height: sizeTokens.iconMedium,
    display: 'block',
  },
  
  largeIcon: {
    width: sizeTokens.iconLarge,
    height: sizeTokens.iconLarge,
    display: 'block',
  },
  
  smallIcon: {
    width: sizeTokens.iconSmall,
    height: sizeTokens.iconSmall,
    display: 'block',
  },
  
  divider: {
    width: sizeTokens.dividerWidth,
    height: sizeTokens.dividerHeight,
    backgroundColor: colorTokens.stroke2,
    borderRadius: tokens.borderRadiusSmall,
    margin: `${spacingTokens.spacingVerticalNone} ${spacingTokens.spacingHorizontalXS}`,
    flexShrink: 0,
  },
  
  // Responsive commands - hide on smaller viewports  
  responsiveCommand: {
    [`@media (max-width: ${breakpointTokens.desktop})`]: {
      display: 'none !important',
    },
  },
  
  // Zoom dropdown styles - matches the image provided
  zoomDropdownContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '28px',
    minWidth: '56px',
    padding: '4px 8px',
    backgroundColor: colorTokens.neutralBackground1,
    border: `1px solid ${colorTokens.stroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    gap: '4px',
    '&:hover': {
      backgroundColor: colorTokens.neutralBackground2,
    },
  },
  
  zoomDropdownText: {
    fontSize: typographyTokens.fontSizeBase300,
    fontFamily: typographyTokens.fontFamilyBase,
    fontWeight: typographyTokens.fontWeightRegular,
    color: colorTokens.neutralForeground1,
    lineHeight: typographyTokens.lineHeightBase300,
    whiteSpace: 'nowrap',
  },
  
  dropdownChevron: {
    width: '12px',
    height: '12px',
    display: 'block',
    marginLeft: '2px',
  },
});

interface WordRibbonProps {
  onCopilotClick?: () => void;
  isCopilotPaneOpen?: boolean;
}

export const WordRibbon: React.FC<WordRibbonProps> = ({ onCopilotClick: _onCopilotClick, isCopilotPaneOpen: _isCopilotPaneOpen }) => {
  console.log('WordRibbon component rendering...', { _onCopilotClick, _isCopilotPaneOpen });
  
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState('Home');
  
  // Get real Slate editor commands and version (version triggers re-renders)
  const { commands: realCommands, version } = useWordSlateEditor();
  
  // Log version changes for debugging
  console.log('WordRibbon version:', version);
  
  // Use real commands instead of alerts
  const editorCommands = {
    undo: () => realCommands.undo(),
    redo: () => realCommands.redo(),
    toggleBold: () => realCommands.toggleBold(),
    toggleItalic: () => realCommands.toggleItalic(),
    toggleUnderline: () => realCommands.toggleUnderline(),
    toggleStrikethrough: () => realCommands.toggleStrikethrough(),
    toggleBulletedList: () => realCommands.toggleBulletedList(),
    toggleNumberedList: () => realCommands.toggleNumberedList(),
    toggleAlignLeft: () => realCommands.toggleAlignLeft(),
    toggleAlignCenter: () => realCommands.toggleAlignCenter(),
    toggleAlignRight: () => realCommands.toggleAlignRight(),
    toggleAlignJustify: () => realCommands.toggleAlignJustify(),
    increaseFontSize: () => realCommands.increaseFontSize(),
    decreaseFontSize: () => realCommands.decreaseFontSize(),
    decreaseIndent: () => realCommands.decreaseIndent(),
    increaseIndent: () => realCommands.increaseIndent(),
  };
  
  // Get active states for buttons
  const isBoldActive = realCommands.isBoldActive();
  const isItalicActive = realCommands.isItalicActive();
  const isUnderlineActive = realCommands.isUnderlineActive();
  const isStrikethroughActive = realCommands.isStrikethroughActive();
  const isBulletedListActive = realCommands.isBulletedListActive();
  const isNumberedListActive = realCommands.isNumberedListActive();
  const alignment = realCommands.getAlignment();

  const tabs = ['File', 'Home', 'Insert', 'Layout', 'References', 'Review', 'View', 'Help'];

  // Function to get responsive hiding class for each tab
  const getTabHidingClass = (_tab: string) => {
    // Tab hiding styles were removed - Fluent UI TabList handles responsive behavior
    // Return empty string to allow default Fluent UI styling to show through
    return '';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Home':
        return (
          <>
            {/* Undo/Redo */}
            <div className={styles.ribbonGroup}>
              <MenuButton 
                appearance="subtle" 
                className={styles.ribbonButton}
                onClick={() => editorCommands.undo()}
              >
                <img src="/src/assets/office/ribbon/Undo.svg" alt="Undo" className={styles.icon} />
              </MenuButton>
            </div>

            {/* Clipboard */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/ShowClipboard.svg" alt="Paste" className={styles.icon} />
              </MenuButton>
              
              <Button appearance="subtle" className={`${styles.ribbonButton} ${styles.responsiveCommand}`}>
                <img src="/src/assets/office/ribbon/FormatPainter.svg" alt="Format Painter" className={styles.icon} />
              </Button>
            </div>

            <div className={styles.divider} />

            {/* Font Controls */}
            <div className={styles.ribbonGroup} style={{ gap: spacingTokens.spacingHorizontalXXS }}>
              {/* Connected Font and Font Size Controls */}
              <div style={{ display: 'flex', height: '32px' }}>
                <div style={{
                  width: '136px',
                  height: '32px',
                  fontSize: '14px',
                  padding: '7px 4px 7px 4px',
                  border: '1px solid #d1d1d1',
                  borderRadius: '4px 0 0 4px',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: '#242424',
                  fontFamily: '"Segoe UI", sans-serif',
                  lineHeight: '20px',
                  cursor: 'pointer',
                  gap: '6px'
                }}>
                  <span style={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    whiteSpace: 'nowrap', 
                    textOverflow: 'ellipsis' 
                  }}>Aptos (Body)</span>
                  <img 
                    src="/src/assets/office/ribbon/CommonControlChevronDown.svg" 
                    alt="Font dropdown" 
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      flexShrink: 0, 
                      marginLeft: '2px' 
                    }}
                  />
                </div>
                <div style={{
                  width: '56px',
                  height: '32px',
                  fontSize: '14px',
                  padding: '7px 4px 7px 4px',
                  border: '1px solid #d1d1d1',
                  borderLeft: 'none',
                  borderRadius: '0 4px 4px 0',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: '#242424',
                  fontFamily: '"Segoe UI", sans-serif',
                  lineHeight: '20px',
                  cursor: 'pointer',
                  gap: '6px'
                }}>
                  <span style={{ 
                    width: '20px', 
                    textAlign: 'center', 
                    whiteSpace: 'nowrap' 
                  }}>11</span>
                  <img 
                    src="/src/assets/office/ribbon/CommonControlChevronDown.svg" 
                    alt="Font size dropdown" 
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      flexShrink: 0, 
                      marginLeft: '2px' 
                    }}
                  />
                </div>
              </div>

              <Button 
                appearance="subtle" 
                className={`${styles.ribbonButton} ${styles.responsiveCommand}`}
                onClick={editorCommands.increaseFontSize}
              >
                <img src="/src/assets/office/ribbon/GrowFont.svg" alt="Increase Font Size" className={styles.icon} />
              </Button>
              
              <Button 
                appearance="subtle" 
                className={`${styles.ribbonButton} ${styles.responsiveCommand}`}
                onClick={editorCommands.decreaseFontSize}
              >
                <img src="/src/assets/office/ribbon/ShrinkFont.svg" alt="Decrease Font Size" className={styles.icon} />
              </Button>
              
              <ToggleButton 
                appearance="subtle" 
                className={styles.ribbonButton}
                checked={isBoldActive}
                onClick={() => {
                  console.log('Bold button clicked');
                  editorCommands.toggleBold();
                }}
              >
                <img src="/src/assets/office/ribbon/Bold.svg" alt="Bold" className={styles.icon} />
              </ToggleButton>
              
              <ToggleButton 
                appearance="subtle" 
                className={styles.ribbonButton}
                checked={isItalicActive}
                onClick={() => {
                  console.log('Italic button clicked');
                  editorCommands.toggleItalic();
                }}
              >
                <img src="/src/assets/office/ribbon/italic.svg" alt="Italic" className={styles.icon} />
              </ToggleButton>

              <ToggleButton 
                appearance="subtle" 
                className={styles.ribbonButton}
                checked={isUnderlineActive}
                onClick={() => editorCommands.toggleUnderline()}
              >
                <img src="/src/assets/office/ribbon/Underline.svg" alt="Underline" className={styles.icon} />
              </ToggleButton>

              <ToggleButton 
                appearance="subtle" 
                className={`${styles.ribbonButton} ${styles.responsiveCommand}`}
                checked={isStrikethroughActive}
                onClick={() => editorCommands.toggleStrikethrough()}
              >
                <img src="/src/assets/office/ribbon/Strikethrough.svg" alt="Strikethrough" className={styles.icon} />
              </ToggleButton>
              
              <MenuButton appearance="subtle" className={`${styles.ribbonMenuButton} ${styles.responsiveCommand}`}>
                <img src="/src/assets/office/ribbon/HighlightWithColor.svg" alt="Highlight Color" className={styles.icon} />
              </MenuButton>
              
              <MenuButton appearance="subtle" className={`${styles.ribbonMenuButton} ${styles.responsiveCommand}`}>
                <img src="/src/assets/office/ribbon/FontColor.svg" alt="Font Color" className={styles.icon} />
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* Paragraph */}
            <div className={styles.ribbonGroup}>
              <ToggleButton 
                appearance="subtle" 
                className={styles.ribbonButton}
                checked={isBulletedListActive}
                onClick={() => editorCommands.toggleBulletedList()}
              >
                <img src="/src/assets/office/ribbon/Bullets.svg" alt="Bullets" className={styles.icon} />
              </ToggleButton>
              
              <ToggleButton 
                appearance="subtle" 
                className={`${styles.ribbonButton} ${styles.responsiveCommand}`}
                checked={isNumberedListActive}
                onClick={() => editorCommands.toggleNumberedList()}
              >
                <img src="/src/assets/office/ribbon/Numbering.svg" alt="Numbering" className={styles.icon} />
              </ToggleButton>

              <Button appearance="subtle" className={`${styles.ribbonButton} ${styles.responsiveCommand}`}>
                <img src="/src/assets/office/ribbon/BulletChecklist.svg" alt="Checklist" className={styles.icon} />
              </Button>

              <Button 
                appearance="subtle" 
                className={`${styles.ribbonButton} ${styles.responsiveCommand}`}
                onClick={editorCommands.decreaseIndent}
              >
                <img src="/src/assets/office/ribbon/Outdent.svg" alt="Decrease Indent" className={styles.icon} />
              </Button>

              <Button 
                appearance="subtle" 
                className={`${styles.ribbonButton} ${styles.responsiveCommand}`}
                onClick={editorCommands.increaseIndent}
              >
                <img src="/src/assets/office/ribbon/Indent.svg" alt="Increase Indent" className={styles.icon} />
              </Button>

              <Button 
                appearance="subtle" 
                className={`${styles.ribbonButton} ${styles.responsiveCommand}`}
                onClick={() => {
                  // Cycle through alignments: left -> center -> right -> justify -> left
                  if (alignment === 'center') {
                    editorCommands.toggleAlignRight();
                  } else if (alignment === 'right') {
                    editorCommands.toggleAlignJustify();
                  } else if (alignment === 'justify') {
                    editorCommands.toggleAlignLeft();
                  } else {
                    editorCommands.toggleAlignCenter();
                  }
                }}
                title={`Align ${alignment || 'left'}`}
              >
                <img 
                  src="/src/assets/office/ribbon/LeftAlign.svg"
                  alt={`Align ${alignment || 'left'}`} 
                  className={styles.icon} 
                />
              </Button>

              <MenuButton appearance="subtle" className={`${styles.ribbonMenuButton} ${styles.responsiveCommand}`}>
                <img src="/src/assets/office/ribbon/LineSpacing.svg" alt="Line Spacing" className={styles.icon} />
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* Styles */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/UxGalWordQuickFormats.svg" alt="Styles" className={styles.icon} />
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* Voice Dictation */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/Voicedictation.svg" alt="Dictate" className={styles.icon} />
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* Editor */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Editor.svg" alt="Editor" className={styles.icon} />
              </Button>
            </div>

            <div className={styles.divider} />

            {/* More */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Ellipsis.svg" alt="More" className={styles.icon} />
              </Button>
            </div>

            <div className={styles.divider} />
          </>
        );

      case 'Insert':
        return (
          <>
            {/* Page Break */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/PageBreak.svg" alt="Page Break" className={styles.icon} />
                <span className={styles.buttonLabel}>Page Break</span>
              </Button>
            </div>

            {/* Table */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/Table.svg" alt="Table" className={styles.icon} />
                <span className={styles.buttonLabel}>Table</span>
              </MenuButton>
            </div>

            {/* Picture */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/Picture.svg" alt="Picture" className={styles.icon} />
                <span className={styles.buttonLabel}>Picture</span>
              </MenuButton>
            </div>

            {/* Drawing */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/DrawingCanvas.svg" alt="Drawing" className={styles.icon} />
                <span className={styles.buttonLabel}>Drawing</span>
              </Button>
            </div>

            {/* Link */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/ShareasLink.svg" alt="Link" className={styles.icon} />
              </MenuButton>
            </div>

            {/* Table of Contents */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/MSWordInsertTableOfContents.svg" alt="Table of Contents" className={styles.icon} />
                <span className={styles.buttonLabel}>Table of Contents</span>
              </Button>
            </div>

            {/* Bookmarks */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/AudioBookmark.svg" alt="Bookmarks" className={styles.icon} />
              </MenuButton>
            </div>

            {/* Online Video */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/InsertMovieFromGallery.svg" alt="Online Video" className={styles.icon} />
              </Button>
            </div>

            {/* New Comment */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/NewComment.svg" alt="New Comment" className={styles.icon} />
              </Button>
            </div>

            {/* Header & Footer */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/HeaderAndFooter.svg" alt="Header & Footer" className={styles.icon} />
                <span className={styles.buttonLabel}>Header & Footer</span>
              </Button>
            </div>

            {/* Page Numbers */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/PageNumber.svg" alt="Page Numbers" className={styles.icon} />
                <span className={styles.buttonLabel}>Page Numbers</span>
              </MenuButton>
            </div>

            {/* Math */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/MathZone.svg" alt="Math" className={styles.icon} />
              </Button>
            </div>

            {/* Symbols */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/Symbol.svg" alt="Symbols" className={styles.icon} />
              </MenuButton>
            </div>

            {/* Emojis */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/EmojiGallery.svg" alt="Emojis" className={styles.icon} />
              </MenuButton>
            </div>
            <div className={styles.divider} />
          </>
        );

      case 'Layout':
        return (
          <>
            {/* Page Setup */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/UxGalPageMargins.svg" alt="Margins" className={styles.icon} />
                <span className={styles.buttonLabel}>Margins</span>
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/UxGalPageOrientation.svg" alt="Orientation" className={styles.icon} />
                <span className={styles.buttonLabel}>Orientation</span>
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/GalPageSize.svg" alt="Size" className={styles.icon} />
                <span className={styles.buttonLabel}>Size</span>
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/PageBreak.svg" alt="Breaks" className={styles.icon} />
                <span className={styles.buttonLabel}>Breaks</span>
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/FlyoutLineNumbers.svg" alt="Line Numbers" className={styles.icon} />
                <span className={styles.buttonLabel}>Line Numbers</span>
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* Paragraph */}
              <div className={styles.ribbonGroup} style={{ alignItems: 'center', gap: spacingTokens.spacingHorizontalS }}>
              <img src="/src/assets/office/ribbon/Indent.svg" alt="Indent Left" className={styles.icon} />
              <SpinButton
                defaultValue={0}
                min={0}
                max={60}
                step={0.1}
                size="small"
                style={{
                  width: '64px',
                  height: sizeTokens.controlHeight
                }}
              />
                <img src="/src/assets/office/ribbon/Outdent.svg" alt="Indent Right" className={styles.icon} />
              <SpinButton
                defaultValue={0}
                min={0}
                max={60}
                step={0.1}
                size="small"
                style={{
                  width: '64px',
                  height: sizeTokens.controlHeight
                }}
              />
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/MSWordOpenParaAbove.svg" alt="Spacing Before" className={styles.icon} />
                <span className={styles.buttonLabel}>Spacing Before</span>
              </Button>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/MSWordOpenParaBelow.svg" alt="Spacing After" className={styles.icon} />
                <span className={styles.buttonLabel}>Spacing After</span>
              </Button>
            </div>

            <div className={styles.divider} />

            {/* Page Background */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/ColorPickerBackground.svg" alt="Page Color" className={styles.icon} />
                <span className={styles.buttonLabel}>Page Color</span>
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* Arrange */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Ellipsis.svg" alt="More" className={styles.icon} />
              </Button>
            </div>
            <div className={styles.divider} />
          </>
        );

      case 'References':
        return (
          <>
            {/* References Group */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/MSWordInsertTableOfContents.svg" alt="Table of Contents" className={styles.icon} />
                <span className={styles.buttonLabel}>Table of Contents</span>
              </MenuButton>

              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Footnote.svg" alt="Insert Footnote" className={styles.icon} />
                <span className={styles.buttonLabel}>Insert Footnote</span>
              </Button>

              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/MSWordInsertEndnoteNow.svg" alt="Insert Endnote" className={styles.icon} />
                <span className={styles.buttonLabel}>Insert Endnote</span>
              </Button>

              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/CitationAddIns.svg" alt="Citations" className={styles.icon} />
                <span className={styles.buttonLabel}>Citations</span>
              </MenuButton>
            </div>

            <div className={styles.divider} />
          </>
        );

      case 'Review':
        return (
          <>
            {/* Editor Group */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/Editor.svg" alt="Editor" className={styles.icon} />
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/Spelling.svg" alt="Spelling & Grammar" className={styles.icon} />
                <span className={styles.buttonLabel}>Spelling & Grammar</span>
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/WordCountList.svg" alt="Word Count" className={styles.icon} />
                <span className={styles.buttonLabel}>Word Count</span>
              </MenuButton>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/AccessibilityChecker.svg" alt="Check Accessibility" className={styles.icon} />
                <span className={styles.buttonLabel}>Check Accessibility</span>
              </Button>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/MarkAllHighlightsAsRead.svg" alt="Mark all as Read" className={styles.icon} />
              </Button>
            </div>

            <div className={styles.divider} />

            {/* Translate */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/Translator.svg" alt="Translate" className={styles.icon} />
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* New Comment, Delete, Previous, Next, Show Comments */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/NewComment.svg" alt="New Comment" className={styles.icon} />
              </Button>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/DeleteComment.svg" alt="Delete" className={styles.icon} />
              </MenuButton>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/PreviousComment.svg" alt="Previous" className={styles.icon} />
              </Button>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/NextComment.svg" alt="Next" className={styles.icon} />
              </Button>
              <ToggleButton appearance="subtle" className={styles.ribbonButton} checked>
                <img src="/src/assets/office/ribbon/Comment.svg" alt="Show Comments" className={styles.icon} />
              </ToggleButton>
            </div>

            <div className={styles.divider} />

            {/* Filter, Markup */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/FilterComments.svg" alt="Filter" className={styles.icon} />
                <span className={styles.buttonLabel}>Filter</span>
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/NewInkComment.svg" alt="Markup" className={styles.icon} />
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* Track Changes, Accept, Reject */}
            <div className={styles.ribbonGroup}>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/TrackChanges.svg" alt="Track Changes" className={styles.icon} />
                <span className={styles.buttonLabel}>Track Changes</span>
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/AcceptRevision.svg" alt="Accept" className={styles.icon} />
              </MenuButton>
              <MenuButton appearance="subtle" className={styles.ribbonMenuButton}>
                <img src="/src/assets/office/ribbon/RejectRevision.svg" alt="Reject" className={styles.icon} />
              </MenuButton>
            </div>

            <div className={styles.divider} />

            {/* More */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Ellipsis.svg" alt="More" className={styles.icon} />
              </Button>
            </div>

            <div className={styles.divider} />
          </>
        );


      case 'View':
        return (
          <>
            {/* Document Views Group */}
            <div className={styles.ribbonGroup}>
              <ToggleButton appearance="subtle" className={styles.ribbonButton} checked>
                <img src="/src/assets/office/ribbon/PageLayoutView.svg" alt="Separate Pages" className={styles.icon} />
                <span className={styles.buttonLabel}>Separate Pages</span>
              </ToggleButton>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Books.svg" alt="Reading View" className={styles.icon} />
                <span className={styles.buttonLabel}>Reading View</span>
              </Button>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/AudioReader.svg" alt="Immersive Reader" className={styles.icon} />
              </Button>
            </div>

            <div className={styles.divider} />

            {/* Zoom Group */}
            <div className={styles.ribbonGroup}>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacingTokens.spacingHorizontalXXS }}>
              <img src="/src/assets/office/ribbon/Search.svg" alt="Zoom" className={styles.icon} />
              <span className={styles.buttonLabel}>Zoom</span>
              <div className={styles.zoomDropdownContainer} style={{ margin: spacingTokens.spacingHorizontalXXS }}>
                <span className={styles.zoomDropdownText}>100%</span>
                <img src="/src/assets/office/ribbon/CommonControlChevronDown.svg" alt="Zoom percentage dropdown" className={styles.dropdownChevron} />
              </div>
              </div>
            </div>

            <div className={styles.divider} />

            {/* Show Group */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Ruler.svg" alt="Ruler" className={styles.icon} />
                <span className={styles.buttonLabel}>Ruler</span>
              </Button>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/MenuNavigationPane.svg" alt="Navigation" className={styles.icon} />
                <span className={styles.buttonLabel}>Navigation</span>
              </Button>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/HeaderAndFooter.svg" alt="Header & Footer" className={styles.icon} />
                <span className={styles.buttonLabel}>Header & Footer</span>
              </Button>
            </div>

            <div className={styles.divider} />

            {/* Comments Group */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Feedback.svg" alt="View with Comments" className={styles.icon} />
                <span className={styles.buttonLabel}>View with Comments</span>
              </Button>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Edit.svg" alt="Edit" className={styles.icon} />
                <span className={styles.buttonLabel}>Edit</span>
              </Button>
            </div>

            <div className={styles.divider} />

            {/* Appearance Group */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/GalContrast.svg" alt="Dark Mode" className={styles.icon} />
                <span className={styles.buttonLabel}>Dark Mode</span>
              </Button>
            </div>

            <div className={styles.divider} />

            {/* More Group */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Ellipsis.svg" alt="More" className={styles.icon} />
              </Button>
            </div>
            
            <div className={styles.divider} />
          </>
        );

      case 'Help':
        return (
          <>
            {/* Help Group */}
            <div className={styles.ribbonGroup}>
              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/PSSHelp.svg" alt="Help" className={styles.icon} />
                <span className={styles.buttonLabel}>Help</span>
              </Button>

              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/OfficeAssistant.svg" alt="Contact Support" className={styles.icon} />
                <span className={styles.buttonLabel}>Contact Support</span>
              </Button>

              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/Feedback.svg" alt="Feedback" className={styles.icon} />
                <span className={styles.buttonLabel}>Feedback</span>
              </Button>

              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/JotKeyboardShortcuts.svg" alt="Keyboard Shortcuts" className={styles.icon} />
                <span className={styles.buttonLabel}>Keyboard Shortcuts</span>
              </Button>

              <Button appearance="subtle" className={styles.ribbonButton}>
                <img src="/src/assets/office/ribbon/WhatsNewRecentUpdates.svg" alt="What's New" className={styles.icon} />
                <span className={styles.buttonLabel}>What's New</span>
              </Button>
            </div>

            <div className={styles.divider} />
          </>
        );

      default:
        return (
          <div className={styles.ribbonGroup}>
            <div style={{ 
              padding: `${spacingTokens.spacingVerticalXS} ${spacingTokens.spacingHorizontalS}`, 
              color: colorTokens.neutralForeground2, 
              fontSize: typographyTokens.fontSizeBase300,
              height: sizeTokens.dividerHeight,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: colorTokens.neutralBackground2,
              borderRadius: tokens.borderRadiusMedium,
              border: `${tokens.strokeWidthThin} solid ${colorTokens.stroke1}`
            }}>
              {activeTab} tab content coming soon...
            </div>
          </div>
        );
    }
  };

  return (
    <div className={styles.container}>
      {/* Tab Bar with Collaboration Corner */}
      <div className={styles.tabBar}>
        <TabList selectedValue={activeTab} onTabSelect={(_, data) => data.value !== 'File' && setActiveTab(data.value as string)} className={styles.tabList}>
          {tabs.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              className={`${styles.tab} ${getTabHidingClass(tab)}`}
              disabled={tab === 'File'}
            >
              {tab}
            </Tab>
          ))}
        </TabList>
        
        <div className={styles.collabCorner}>
          {/* Avatar Group */}
          <div style={{ height: '16px', display: 'flex', alignItems: 'center'}}>
            <AvatarGroup size={20} layout="spread" className={styles.avatarGroup}>
              <AvatarGroupItem
                name="Megan B."
                image={{ src: "/src/assets/avatars/MeganB.jpg" }}
              />
              <AvatarGroupItem
                name="Robert T."
                image={{ src: "/src/assets/avatars/robert-tolbert.jpg" }}
              />
              <AvatarGroupItem name="M">M</AvatarGroupItem>
            </AvatarGroup>
          </div>
          
          <Button appearance="secondary" size="small" className={styles.collabButton}>
            <img src="/src/assets/office/ribbon/Comment.svg" alt="Comments" className={styles.smallIcon} />
            <span className={styles.collabButtonLabel}>Comments</span>
          </Button>
          
          <Button appearance="secondary" size="small" className={styles.collabButton}>
            <img src="/src/assets/office/ribbon/Activity.svg" alt="Catch Up" className={styles.smallIcon} />
            <span className={styles.collabButtonLabel}>Catch Up</span>
          </Button>
          
          <MenuButton appearance="secondary" size="small" className={styles.collabButton}>
            <img src="/src/assets/office/ribbon/Edit.svg" alt="Editing" className={styles.smallIcon} />
            <span className={styles.collabButtonLabel}>Editing</span>
          </MenuButton>
          
          <MenuButton appearance="primary" size="small" className={`${styles.collabButton} ${styles.wordPrimaryButton}`}>
            <img src="/src/assets/office/ribbon/Share.svg" alt="Share" className={styles.smallIcon} />
            <span className={styles.shareButtonLabel}>Share</span>
          </MenuButton>
        </div>
      </div>

      {/* Ribbon Content */}
      <div className={styles.ribbonContent}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default WordRibbon;
